import { Apple, Smartphone, Globe, Feather, Repeat, ShieldCheck } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { FadeUp, Eyebrow } from "@/components/site/Motion";
import { CtaBand } from "@/components/site/CtaBand";

const PLATFORMS = [
  { icon: Apple, name: "Apple App Store", label: "Primary", text: "Most of what we make is built for iPhone and iPad first.", w: "w-full" },
  { icon: Smartphone, name: "Google Play Store", label: "Primary", text: "Our apps also ship to Android, so more people can use them.", w: "w-full" },
  { icon: Globe, name: "Web apps", label: "Occasionally", text: "Some ideas work best in a browser. When they do, we build for the web.", w: "w-1/3" },
];

const PRINCIPLES = [
  { icon: Feather, title: "Simple first", text: "Clean screens, clear buttons, no clutter. If it needs a manual, we rethink it." },
  { icon: Repeat, title: "Always improving", text: "Launch is the start. We keep updating our apps based on what users tell us." },
  { icon: ShieldCheck, title: "Respect for users", text: "We build apps we would be happy to have on our own phones." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        testId="about-hero"
        eyebrow="About us"
        lines={["An app studio", <span className="text-[#0D6EFD]">that builds for itself.</span>]}
        intro="Aries App Station Inc. is an independent company that creates its own apps. We primarily build for the Apple App Store and Google Play Store, and occasionally we build web apps."
      />

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <Eyebrow>Our story</Eyebrow>
            <h2 className="font-display mt-5 text-3xl font-extrabold leading-[1.05] tracking-tight text-[#0B132B] sm:text-4xl">Our ideas, our apps, our name on the listing.</h2>
          </FadeUp>
          <FadeUp delay={0.1} className="space-y-5 text-base leading-relaxed text-slate-600 md:text-lg lg:col-span-7">
            <p>We are not an agency. We do not build apps for other companies. Every product you see from Aries App Station is one we came up with, designed and published ourselves.</p>
            <p>Our home is the app stores. We focus on iPhone and Android apps because that is where people spend their time, and we reach for the web when a project is better suited to it.</p>
            <p>Our portfolio is growing. Trailer API and Trivia Pop are out, and more apps are already in the works.</p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="about-platforms" className="grain relative bg-[#0A0F1D] py-24 sm:py-32">
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeUp>
            <Eyebrow dark>Where you will find us</Eyebrow>
            <h2 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">Mostly the app stores.</h2>
          </FadeUp>
          <div className="mt-14 divide-y divide-white/10 border-y border-white/10">
            {PLATFORMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <FadeUp key={p.name} delay={i * 0.08} className="grid items-center gap-6 py-8 md:grid-cols-12">
                  <div className="flex items-center gap-4 md:col-span-4">
                    <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-white"><Icon className="h-6 w-6" /></span>
                    <span className="font-display text-2xl font-bold text-white">{p.name}</span>
                  </div>
                  <p className="text-sm text-slate-300 md:col-span-4 md:text-base">{p.text}</p>
                  <div className="md:col-span-4">
                    <p className="font-mono text-xs uppercase tracking-wider text-[#7fb2ff]">{p.label}</p>
                    <div className="mt-2 h-1.5 w-full rounded-full bg-white/10"><div className={`h-full rounded-full bg-[#0D6EFD] ${p.w}`} /></div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <FadeUp>
          <Eyebrow>What we believe</Eyebrow>
          <h2 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-[#0B132B] sm:text-4xl lg:text-5xl">Three things we hold on to.</h2>
        </FadeUp>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return (
              <FadeUp key={p.title} delay={i * 0.08} className="group rounded-[1.75rem] border border-slate-200 bg-white p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-30px_rgba(13,110,253,0.45)]">
                <Icon className="h-7 w-7 text-[#0D6EFD]" />
                <h3 className="font-display mt-6 text-2xl font-bold text-[#0B132B]">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{p.text}</p>
              </FadeUp>
            );
          })}
        </div>
      </section>

      <CtaBand title="Want to know what we are building next?" text="Drop us a line. We are always happy to hear from people who use our apps." />
    </div>
  );
}
